import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { BrowserRouter, matchPath } from 'react-router-dom'
import App from './App'
import { PITCH_DECK_RENDER_PATH } from './routes/RenderRoutes'
import ErrorBoundary from './components/core/ErrorBoundary'
import * as serviceWorker from './serviceWorker'
import { SENTRY_ENVIRONMENT, apiTraceTarget } from './services/sentry'
import { classifyAlert, HEADLESS_RENDER_SURFACE } from './services/alert-severity'
import { isStaleChunkEvent, recoverFromStaleChunk } from './utils/chunkLoadRecovery'
import { installDomSafetyPatches } from './utils/domSafetyPatches'

// Must run before the first React render so every commit-phase DOM call is
// protected from external mutations (Google Translate, extensions).
installDomSafetyPatches()

// nolan-studio@<semver|sha>, written to .env by infrastructure/create-studio-env-file.js.
const SENTRY_RELEASE = process.env.REACT_APP_SENTRY_RELEASE

// The pitch-deck PDF Lambda's headless Chrome: anonymous, and the Lambda reports render failures
// with the user, so its events are tagged out of critical and never record a replay.
const HEADLESS_RENDER = Boolean(matchPath(PITCH_DECK_RENDER_PATH, window.location.pathname))

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: SENTRY_ENVIRONMENT,
  // Omitted when unset: an explicit undefined would also drop the release the webpack plugin injects.
  ...(SENTRY_RELEASE ? { release: SENTRY_RELEASE } : {}),
  integrations: [
    Sentry.browserTracingIntegration(),
    ...(HEADLESS_RENDER ? [] : [Sentry.replayIntegration()])
  ],
  ...(HEADLESS_RENDER ? { initialScope: { tags: { surface: HEADLESS_RENDER_SURFACE } } } : {}),
  tracesSampleRate: 0.2,
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost', /^https:\/\/nolanai\.app\/api/, apiTraceTarget(process.env.REACT_APP_REST_API_HOST)].filter(Boolean),
  // Replays only for sessions that hit an error.
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  // The user's own connectivity, not an app failure.
  ignoreErrors: ['auth/network-request-failed'],
  beforeSend (event) {
    // Chunk-load failures from tabs running a previous deployment are not
    // actionable code errors — reload the tab onto the new build instead
    // of reporting them.
    if (isStaleChunkEvent(event)) {
      recoverFromStaleChunk()
      return null
    }
    return classifyAlert(event)
  }
})
// After
const container = document.getElementById('root')
const root = createRoot(container) // createRoot(container!) if you use TypeScript
root.render(
  <ErrorBoundary>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </ErrorBoundary>
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
